// Project data for the v3 landing page.
// Each entry uses the new metadata shape:
//   - title       : main project label (rendered ink-black in the rail)
//   - subtitle    : trailing tag rendered in grey next to the title
//   - description : single-line description shown in the rail
//   - year        : 4-digit year shown top-right of the rail
//   - role        : single role label (uppercase mono in the rail)
//   - tools       : comma-joined tools list (uppercase mono in the rail)
//   - category    : v3 filter bucket — "Computational" | "Product"
//   - categories  : legacy field kept for any downstream consumers
//
// `mediaSrc` / `hoverMediaSrc` / `targetUrl` are unchanged from v2 so all
// existing project pages keep routing correctly.

const baseProjects = [
  {
    title: "AquaCast",
    subtitle: "drone-deployable water sampler.",
    description:
      "A modular, drone-deployable multi-depth water sampling system — lab-ready physical samples plus automatic metadata, without sending people out by boat.",
    year: 2026,
    role: "Researcher · Designer",
    tools: ["Hardware", "ESP32-C3", "Field research"],
    mediaType: "image",
    mediaSrc: "/projects/AquaCast/images/mockup_01.png",
    hoverMediaSrc: "/projects/AquaCast/images/prototype_01.jpg",
    targetUrl: "/AquaCast",
    category: "Product",
    categories: ["Product"],
    projectCardSize: "medium",
  },
  {
    title: "SILT Middelkerke",
    subtitle: "landmark facade.",
    description: "Landmark timber facade study for the SILT bathing pavilion in Middelkerke, Belgium.",
    year: 2023,
    role: "Designer",
    tools: ["Grasshopper", "Rhino"],
    mediaType: "image",
    mediaSrc: "/projects/SILTMiddelkerke/images/AllOutput.png",
    hoverMediaSrc: "/projects/SILTMiddelkerke/images/Output8.png",
    targetUrl: "/SILTMiddelkerke",
    category: "Computational",
    categories: ["Computational"],
    projectCardSize: "medium",
  },
  {
    title: "The Cutaway",
    subtitle: "community space.",
    description: "Computational study for a civic anchor at ground level in Barangaroo, Australia.",
    year: 2024,
    role: "Designer",
    tools: ["Grasshopper"],
    mediaType: "image",
    mediaSrc: "/projects/The Cutaway/Part Types/Frame_00014.png",
    hoverMediaSrc: "/projects/The Cutaway/Card_ext render.jpeg",
    targetUrl: "/TheCutaway",
    category: "Computational",
    categories: ["Computational"],
    projectCardSize: "medium",
  },
  {
    title: "Four Seasons Facade",
    subtitle: "performance skin.",
    description: "Parametric facade panelisation tuned to the Dubai sun envelope.",
    year: 2022,
    role: "Designer",
    tools: ["Grasshopper", "Rhino"],
    mediaType: "image",
    mediaSrc: "/projects/Four Seasons Facade System/images/Card_Image.png",
    hoverMediaSrc: "/projects/Four Seasons Facade System/images/image (15)-PSed.png",
    targetUrl: "/FourSeasonsFacade",
    category: "Computational",
    categories: ["Computational"],
    invert: true,
    projectCardSize: "medium",
  },
  {
    title: "ECHO",
    subtitle: "automated geometry.",
    description: "Automated building systems design and geometry generation pipeline.",
    year: 2024,
    role: "Designer · Researcher",
    tools: ["Grasshopper", "Python"],
    mediaType: "image",
    mediaSrc: "/projects/ECHO/images/Arch System-logo.png",
    hoverMediaSrc: "/projects/ECHO/images/Card_r12.png",
    targetUrl: "/ECHO",
    category: "Computational",
    categories: ["Computational"],
    projectCardSize: "medium",
  },
  {
    title: "Cliff Youth Center",
    subtitle: "adaptive louvers.",
    description: "Application of an adaptive louvers system for a youth center on a coastal cliff.",
    year: 2021,
    role: "Designer",
    tools: ["Grasshopper", "Rhino"],
    mediaType: "image",
    mediaSrc: "/projects/Cliff Youth Center/images/Card_Louvers.png",
    hoverMediaSrc: "/projects/Cliff Youth Center/images/ArchSystemIcon.png",
    targetUrl: "/CliffYouthCenter",
    category: "Computational",
    categories: ["Computational"],
    projectCardSize: "medium",
  },
  {
    title: "WEAVE",
    subtitle: "extinction museum.",
    description: "A speculative museum of extinction expressed through woven structural form.",
    year: 2020,
    role: "Designer",
    tools: ["Grasshopper", "Rhino"],
    mediaType: "image",
    mediaSrc: "/projects/WEAVE/images/exploded-removebg-preview.png",
    hoverMediaSrc: "/projects/WEAVE/images/renders/Image45.png",
    targetUrl: "/WEAVE",
    category: "Computational",
    categories: ["Computational"],
    invert: true,
    projectCardSize: "medium",
  },
  {
    title: "APE Tools",
    subtitle: "designer ui kit.",
    description: "A small UI toolkit for Grasshopper aimed at parametric designers.",
    year: 2023,
    role: "Designer · Developer",
    tools: ["C#", "Grasshopper SDK"],
    mediaType: "image",
    mediaSrc: "/projects/APETools/images/APE logo.PNG",
    hoverMediaSrc: "/projects/APETools/images/Radial Grid Screenshot.PNG",
    targetUrl: "/APETools",
    category: "Product",
    categories: ["Product"],
    projectCardSize: "medium",
  },
  {
    title: "CTEP",
    subtitle: "tangible interface.",
    description: "Modular robotic system for 3D input and output as a tangible interface.",
    year: 2021,
    role: "Designer · Researcher",
    tools: ["Robotics", "Rhino"],
    mediaType: "image",
    mediaSrc: "/projects/CTEP/images/ViewCapture20211227_131540.png",
    hoverMediaSrc: "/projects/CTEP/images/CTEP Flip.gif",
    hoverMediaType: "gif",
    targetUrl: "/CTEP",
    category: "Product",
    categories: ["Product"],
    projectCardSize: "medium",
  },
  {
    title: "4D Droplets Clash",
    subtitle: "field study.",
    description: "Abstraction study of magnetic field interactions in 4 dimensions.",
    year: 2016,
    role: "Designer",
    tools: ["Grasshopper", "Simulation"],
    mediaType: "image",
    mediaSrc: "/projects/Droplets Clash/images/Card_mix_1c.png",
    hoverMediaSrc: "/projects/Droplets Clash/images/Model Images/20161121_062135-1.jpg",
    targetUrl: "/DropletsClash",
    category: "Computational",
    categories: ["Computational"],
    invert: true,
    projectCardSize: "medium",
  },
  {
    title: "Cowes Week Pavilion",
    subtitle: "robotic tensile.",
    description: "Robotic tensile pavilion construction prototyped at architectural scale.",
    year: 2019,
    role: "Designer",
    tools: ["Robotics", "Grasshopper"],
    mediaType: "image",
    mediaSrc: "/projects/Cowes Week Pavilion/images/robot 2.png",
    hoverMediaSrc: "/projects/Cowes Week Pavilion/images/renders/Card_Image9.png",
    targetUrl: "/CowesWeekPavilion",
    category: "Computational",
    categories: ["Computational"],
    projectCardSize: "medium",
  },
  {
    title: "Facade Panelization",
    subtitle: "GENx solution.",
    description: "GENx panelisation engine for non-typical facades, balancing geometry and fabrication.",
    year: 2022,
    role: "Designer · Developer",
    tools: ["Grasshopper", "C#"],
    mediaType: "image",
    mediaSrc: "/projects/GENx Facade Solution/images/combined.jpg",
    hoverMediaSrc: "/projects/GENx Facade Solution/images/Non typical 1.png",
    targetUrl: "/GENxFacadeSolution",
    category: "Product",
    categories: ["Computational", "Product"],
    invert: true,
    projectCardSize: "medium",
  },
];

const projects = [...baseProjects];

export { baseProjects };
export default projects;
